// WebSocket 实时通知服务

interface NotificationMessage {
  id: string
  type: 'complaint' | 'notification' | 'system'
  title: string
  message: string
  data?: any
  timestamp: number
  priority: 'low' | 'normal' | 'high' | 'urgent'
  read?: boolean
}

class WebSocketService {
  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectInterval = 5000
  private heartbeatInterval: NodeJS.Timeout | null = null
  private isManualClose = false
  private listeners: Map<string, ((data: any) => void)[]> = new Map()

  constructor() {
    this.setupEventListeners()
  }

  // 连接WebSocket
  connect() {
    const enabled = (import.meta as any).env?.VITE_WS_ENABLED === 'true'
    if (!enabled) return
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return
    }

    const token = localStorage.getItem('token')
    const wsUrl = (import.meta as any).env?.VITE_WS_URL || `ws://${location.hostname}:8787`
    const url = `${wsUrl}/ws/admin?token=${token}`

    try {
      this.ws = new WebSocket(url)
      this.setupWebSocketEvents()
    } catch (error) {
      console.error('WebSocket连接失败:', error)
      this.handleReconnect()
    }
  }

  // 设置WebSocket事件监听
  private setupWebSocketEvents() {
    if (!this.ws) return

    this.ws.onopen = () => {

      this.reconnectAttempts = 0
      this.startHeartbeat()
    }

    this.ws.onmessage = (event) => {
      this.handleMessage(event)
    }

    this.ws.onclose = () => {

      this.stopHeartbeat()

      if (!this.isManualClose) {
        this.handleReconnect()
      }
    }

    this.ws.onerror = (error) => {
      console.error('WebSocket错误:', error)
    }
  }

  // 处理接收到的消息
  private handleMessage(event: MessageEvent) {
    try {
      const data = JSON.parse(event.data) as NotificationMessage

      // 广播消息给所有监听器
      this.broadcast(data.type, data)

      // 显示通知
      this.showNotification(data)

    } catch (error) {
      console.error('解析WebSocket消息失败:', error)
    }
  }

  // 广播消息给特定类型的监听器
  private broadcast(type: string, data: any) {
    const listeners = this.listeners.get(type) || []
    const allListeners = this.listeners.get('*') || []

    // 通知特定类型监听器
    listeners.forEach(callback => {
      try {
        callback(data)
      } catch (error) {
        console.error('执行WebSocket监听器回调失败:', error)
      }
    })

    // 通知所有监听器
    allListeners.forEach(callback => {
      try {
        callback(data)
      } catch (error) {
        console.error('执行WebSocket监听器回调失败:', error)
      }
    })
  }

  // 显示浏览器通知
  private showNotification(data: NotificationMessage) {
    // 浏览器原生通知
    if (Notification.permission === 'granted') {
      const notification = new Notification(data.title, {
        body: data.message,
        icon: this.getNotificationIcon(data.type),
        tag: data.id,
        requireInteraction: data.priority === 'urgent',
      })

      // 点击通知时的处理
      notification.onclick = () => {
        notification.close()
        this.handleNotificationClick(data)
      }

      // 自动关闭
      if (data.priority !== 'urgent') {
        setTimeout(() => {
          notification.close()
        }, 5000)
      }
    }

    // 自定义通知（可以使用 react-hot-toast）
    this.showToastNotification(data)
  }

  // 显示Toast通知
  private showToastNotification(data: NotificationMessage) {
    const { toast } = require('react-hot-toast')

    const toastOptions = {
      duration: data.priority === 'urgent' ? 10000 : 5000,
      icon: this.getToastIcon(data.type),
      style: this.getToastStyle(data.priority),
    }

    toast(data.message, {
      ...toastOptions,
      title: data.title,
    })
  }

  // 获取通知图标
  private getNotificationIcon(type: string): string {
    switch (type) {
      case 'complaint':
        return '/icons/complaint.png'
      case 'notification':
        return '/icons/notification.png'
      case 'system':
        return '/icons/system.png'
      default:
        return '/icons/default.png'
    }
  }

  // 获取Toast图标
  private getToastIcon(type: string): string {
    switch (type) {
      case 'complaint':
        return '⚠️'
      case 'notification':
        return '📢'
      case 'system':
        return '🔔'
      default:
        return 'ℹ️'
    }
  }

  // 获取Toast样式
  private getToastStyle(priority: string) {
    switch (priority) {
      case 'urgent':
        return {
          background: '#ef4444',
          color: '#ffffff',
          borderLeft: '4px solid #dc2626',
        }
      case 'high':
        return {
          background: '#f59e0b',
          color: '#ffffff',
          borderLeft: '4px solid #d97706',
        }
      case 'normal':
        return {
          background: '#3b82f6',
          color: '#ffffff',
          borderLeft: '4px solid #2563eb',
        }
      default:
        return {
          background: '#6b7280',
          color: '#ffffff',
          borderLeft: '4px solid #4b5563',
        }
    }
  }

  // 处理通知点击
  private handleNotificationClick(data: NotificationMessage) {
    // 根据通知类型导航到对应页面
    switch (data.type) {
      case 'complaint':
        window.location.hash = '/complaints'
        break
      case 'notification':
        window.location.hash = '/notifications'
        break
      case 'system':
        // 系统通知可能不需要特殊处理
        break
    }
  }

  // 添加事件监听器
  addListener(type: string, callback: (data: any) => void) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, [])
    }
    this.listeners.get(type)!.push(callback)

    // 返回取消监听的函数
    return () => {
      const listeners = this.listeners.get(type)
      if (listeners) {
        const index = listeners.indexOf(callback)
        if (index > -1) {
          listeners.splice(index, 1)
        }
      }
    }
  }

  // 移除事件监听器
  removeListener(type: string, callback: (data: any) => void) {
    const listeners = this.listeners.get(type)
    if (listeners) {
      const index = listeners.indexOf(callback)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }

  // 发送消息
  send(message: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message))
    } else {
      console.warn('WebSocket未连接，无法发送消息')
    }
  }

  // 开始心跳
  private startHeartbeat() {
    this.stopHeartbeat()

    this.heartbeatInterval = setInterval(() => {
      this.send({ type: 'ping', timestamp: Date.now() })
    }, 30000) // 每30秒发送一次心跳
  }

  // 停止心跳
  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  // 处理重连
  private handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('WebSocket重连次数超过最大限制')
      return
    }

    this.reconnectAttempts++


    setTimeout(() => {
      this.connect()
    }, this.reconnectInterval)
  }

  // 手动关闭连接
  disconnect() {
    this.isManualClose = true
    this.stopHeartbeat()

    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
  }

  // 设置页面可见性变化监听
  private setupEventListeners() {
    // 页面可见性变化时的处理
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopHeartbeat()
      } else {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.startHeartbeat()
        } else {
          this.connect()
        }
      }
    })

    // 页面卸载时的处理
    window.addEventListener('beforeunload', () => {
      this.disconnect()
    })

    // 网络状态变化监听
    window.addEventListener('online', () => {

      this.connect()
    })

    window.addEventListener('offline', () => {

      this.disconnect()
    })
  }

  // 请求通知权限
  async requestNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    }
    return false
  }

  // 检查连接状态
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN
  }

  // 获取连接状态
  getReadyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED
  }
}

// 创建单例实例
const websocketService = new WebSocketService()

// 导出服务
export default websocketService

// 导出类型
export type { NotificationMessage }

// 导出便捷方法
export const connectWebSocket = () => websocketService.connect()
export const disconnectWebSocket = () => websocketService.disconnect()
export const addWebSocketListener = (type: string, callback: (data: any) => void) =>
  websocketService.addListener(type, callback)
export const removeWebSocketListener = (type: string, callback: (data: any) => void) =>
  websocketService.removeListener(type, callback)
export const sendWebSocketMessage = (message: any) => websocketService.send(message)
export const isWebSocketConnected = () => websocketService.isConnected()
export const requestNotificationPermission = () => websocketService.requestNotificationPermission()
